import { DECIMAL } from '@/lib/geographic'
import { sortOrderLabels, sortOrders } from '@/lib/markup-sort/constants'
import { getHeaders, httpGetJson } from '@/lib/net'
import { getUserId, hasValidUserSession } from '@/services/identity-client'
import cloneDeep from 'lodash/cloneDeep'
import debounce from 'lodash/debounce'
import { reactive, toRef, type App, type Ref } from 'vue'

type State = Record<string, unknown>

export default {
  async install(vue: App) {
    // Register $storage synchronously before any awaits so it is available
    // during initial render. Vue's app.use() does not await async install
    // functions, so components can render before the async init below resolves.
    // The storage methods operate on observableState which is already set up.
    vue.config.globalProperties.$storage = {
      // Retrieves raw value from the storage object
      getItem,

      // Retrieves reactive item from storage object
      // This is a returned proxy object. To access
      // the value outside of Vue, it will need to be
      // unpacked from the value property
      getItemRef,

      // Store an item of a registered key. If the key
      // is added to the persistentStorage array. The
      // key and value will be saved to the preferences
      // API.
      saveItem,

      // Helper function for adding a single item in
      // a stored array
      addArrayItem,

      // Helper function for removing a single item in
      // a stored array
      removeArrayItem,
    }

    // There's a possibility that legacyUserId returns the string.sub() function
    // when no token is available. Update code to userId v2 when available.
    const userId = await getUserId()
    const isUserIdString = typeof userId === 'string'
    const userIdString = isUserIdString ? `-${userId}` : ''
    // fish-web is a single-vertical app — always use 'fish' as the storage prefix
    // regardless of URL path, to avoid collisions with webmap's hunt/offroad/backcountry
    // storage keys when running on the same origin (e.g. localhost:3333 dev server).
    const storagePrefix = 'fish'

    // Update local state
    oldStorageKey = `${storagePrefix}${userIdString}`
    storageKey = `${storagePrefix}-app-storage${userIdString}`
    preferencesUrl = window.environment.apiUrl + '/v1/preferences'

    // Migrate old local storage
    migrateOldLocal()

    // Initialize storage
    initializeLocalStorage()
    initializeCloudStorage()

    document.addEventListener('visibilitychange', onVisibilityChange)
  },
}

// Local State
let storageKey: string
let oldStorageKey: string
let preferencesUrl: string
let preferencesResolver: (value: boolean) => void

// Update preferences when page visibility changes to hidden to prevent losing local state
const onVisibilityChange = () => {
  if (document.visibilityState === 'hidden') {
    updatePreferences()
  }
}
// Reduce consecutive calls to preferences endpoint
const updatePreferencesDebounce = debounce(updatePreferences, 1000)

// For functions needing to await initialization of preferences
// Such as updating preferences
const preferencesReady = new Promise<boolean>((r) => (preferencesResolver = r))

// Storage of saved items for overwriting preferences initialization
// This is for items stored before state is updated with preferences
const savedItems: State = {}

// Keys that will be stored in the
// preferences endpoint.
const persistentKeys: string[] = [
  'fishLayers',
  'coordinateType',
  'coordinateToggle',
  'crosshairsToggle',
  'scalebarToggle',
  'secretSpotToggle',
  'unitType',
  'offlineSort',
  'fishSearchHistory',
  'fishSeenAutosubscribe',
  'fishSubmittedOnboardingSteps',
  'seenOnboardingTrailToolTip',
  'seenRouteTour',
  'trial-expiration',
  'layers-activeCollections',
  'layers-activeLayers',
  'layers-migrated',
  'speciesSelected',
  'landSelected',
  'firstSessionInitiated',
  /* Disappearing Tag Ids */
  'contributorPermissionButton',
]

// Reactive state for the storage plugin.
// Add keys here to register them for storage.
// Keys that should exist cross client
// need to also be added to persistentKeys for
// storing in the preferences API
const observableState = reactive<State>({
  // Local storage
  app: null,
  hideRemoveLayerMessage: false,
  myOpenCollection: null,
  libraryOpenCollection: null,
  newElitePurchase: false,
  newPremium: false,
  newAccountState: null,
  newRedeem: false,
  trialActivated: false,
  OfflineDefaultResolution: 5,
  hideOfflineMapNote: false,
  hideLayersRemoveStateModal: false,
  extraSettings: [],
  namesOnWaypoints: false,
  waypointClustering: true,
  waypointWindMarker: null,
  waypointRangeRadius: null,
  'filters-hasPhotos': false,
  'filters-contentOwners': [],
  'filters-sharedWith': [],
  'filters-waypointTypes': [],
  'filters-waypointColors': [],
  'filters-onlyOnMap': false,
  'layers-activeCollections': [],
  'layers-activeLayers': [],
  'layers-migrated': false,
  visibleMarkups: null,
  innovationLabsEnrolled: false,
  innovationLabsFeatures: [],
  'all-content-sort-order': sortOrders.TYPE,
  'waypoint-card-sort-order': sortOrders.NEW_TO_OLD,
  'track-card-sort-order': sortOrders.NEW_TO_OLD,
  'shape-card-sort-order': sortOrders.NEW_TO_OLD,
  'line-card-sort-order': sortOrders.NEW_TO_OLD,
  'all-content-waypoints': true,
  'all-content-tracks': true,
  'all-content-areas': true,
  'all-content-routes': true,
  'all-content-lines': true,
  'markup-type-list-waypoints': true,
  'markup-type-list-tracks': true,
  'markup-type-list-areas': true,
  'markup-type-list-routes': true,
  'markup-type-list-lines': true,
  'share-accept-waypoints': true,
  'share-accept-tracks': true,
  'share-accept-areas': true,
  'share-accept-routes': true,
  'share-accept-lines': true,
  'collection-details-waypoints': true,
  'collection-details-tracks': true,
  'collection-details-areas': true,
  'collection-details-routes': true,
  'collection-details-lines': true,
  'content-select-waypoints': true,
  'content-select-tracks': true,
  'content-select-areas': true,
  'content-select-routes': true,
  'content-select-lines': true,
  'query-content-tab-waypoints': true,
  'query-content-tab-tracks': true,
  'query-content-tab-areas': true,
  'query-content-tab-routes': true,
  'query-content-tab-lines': true,
  'land-areas-waypoints': true,
  'land-areas-tracks': true,
  'land-areas-areas': true,
  'land-areas-routes': true,
  'land-areas-lines': true,
  'route-card-sort-order': null,
  'route-previous-color': null,
  'route-previous-weight': null,
  'route-previous-style': null,
  'trial-expiration': null,
  newAddOnLayer: null,
  tooltipDebug: false,
  fpsMonitor: false,
  'wind-calendar-filters-onlyOnMap': true,
  'wind-forecast-enabled': false,
  // Persistent storage
  fishLayers: [],
  coordinateType: DECIMAL,
  coordinateToggle: true,
  crosshairsToggle: false,
  scalebarToggle: true,
  secretSpotToggle: false,
  unitType: 'imperial',
  basemap: null,
  leafoff: false,
  highfreq: false,
  winteraerial: false,
  offlineSort: sortOrderLabels[sortOrders.NEW_TO_OLD],
  fishSearchHistory: [],
  fishSubmittedOnboardingSteps: [],
  seenOnboardingTrailToolTip: false,
  seenRouteTour: false,
  fishSeenAutosubscribe: false,
  fish_freshwater_basemapImagery: 'default',
  'fish_freshwater_filter_boat-launch': null,
  'fish_freshwater_filter_boat-ramp': null,
  fish_freshwater_filter_fishTypes: null,
  fish_freshwater_filter_amenities: null,
  fish_freshwater_filter_area: null,
  fish_freshwater_filter_trophy: false,
  fish_freshwater_filter_abundance: false,
  fish_freshwater_filter_keeper: false,
  fish_freshwater_filter_stocking_effort: false,
  recently_viewed_is_collapsed: false,
  speciesSelected: null,
  landSelected: null,
  lmsOnboardingStates: null,
  firstSessionInitiated: false,
  fishSetupSelection: [],
  fishSeenTooltips: [],
  /* Disappearing Tag Ids */
  contributorPermissionButton: null,
})

/**
 * Return raw value for item in storage
 *
 * @param key key for stored item
 * @returns value of stored item
 * @throws if key is not allowed
 */
export function getItem<T>(key: string) {
  // Verify key is registered
  if (!isAllowed(key)) {
    throw new Error('Key is not registered in storage plugin: ' + key)
  }

  // clone to avoid side effects on objects
  return cloneDeep(observableState[key]) as T
}

/**
 * Return reactive proxy object for item in storage
 *
 * @param key key for stored item
 * @returns value of stored item
 * @throws if key is not allowed
 */
export function getItemRef<T>(key: string) {
  // Verify key is registered
  if (!isAllowed(key)) {
    throw new Error('Key is not registered in storage plugin: ' + key)
  }

  return toRef(observableState, key) as Ref<T>
}

/**
 * Save item to storage and update reactive state, local state,
 * and preferences if persistent. Include in savedItems for
 * overwriting preferences initialization.
 *
 * @param key Key for locally stored item
 * @param value Value of locally stored item
 */
export async function saveItem(key: string, value: unknown) {
  // Verify key is registered
  if (!isAllowed(key)) {
    throw new Error('Key is not registered in storage plugin: ' + key)
  }

  // Update client storage
  savedItems[key] = value
  updateReactiveState(key, value)
  await updateLocalStorage()

  // Update preferences if persistent
  if (isPersistent(key)) {
    updatePreferencesDebounce()
  }
}

/**
 * Helper function for removing item from a stored array
 * @param key
 * @param value
 */
export function removeArrayItem(key: string, value: unknown) {
  const storedArray = getItem(key)

  if (!Array.isArray(storedArray)) {
    throw new Error('Key is not a stored array: ' + key)
  }

  const updatedArray = storedArray.filter((i) => i !== value)

  return saveItem(key, updatedArray)
}

/**
 * Helper function for adding item from a stored array
 * @param key
 * @param value
 */
export function addArrayItem(key: string, value: unknown) {
  const storedArray = getItem(key)

  // Verify item is an array
  if (!Array.isArray(storedArray)) {
    throw new Error('Key is not a stored array: ' + key)
  }

  // Verify array value doesn't exist
  if (storedArray.includes(value)) {
    return
  }

  storedArray.push(value)

  return saveItem(key, storedArray)
}

/**
 * Update preferences endpoint with latest persistent key values
 */
async function updatePreferences() {
  // Verify user is logged in for update
  const isLoggedIn = await hasValidUserSession()
  if (!isLoggedIn) {
    return
  }

  // Verify preferences is finished successfully and won't
  // overwrite values stored before initialization
  const isPreferencesReady = await preferencesReady
  if (!isPreferencesReady) {
    return
  }

  // Build payload
  const payload: State = {}
  persistentKeys.forEach((key) => {
    payload[key] = observableState[key]
  })

  // Update server storage
  try {
    const headers = await getHeaders()
    const method = 'PUT'
    const body = JSON.stringify({ preferences: payload })
    await fetch(preferencesUrl, {
      headers,
      method,
      body,
      // Ensure that request completes if the page would navigate or close
      keepalive: true,
    })
  } catch (e) {
    throw new Error(`Failed to update preferences to server: ${e}`)
  }
}

/**
 * Copy item to reactive state
 *
 * @param key Key for reactive state
 * @param value Value for reactive state
 */
function updateReactiveState(key: string, value: unknown) {
  observableState[key] = value
}

/**
 * Copy reactive state to local storage
 */
async function updateLocalStorage() {
  // Verify user is logged in for update
  const isLoggedIn = await hasValidUserSession()
  if (!isLoggedIn) {
    return
  }

  const observableStateString = JSON.stringify(observableState)
  localStorage?.setItem(storageKey, observableStateString)
}

/**
 * Load local storage to reactive state
 */
function initializeLocalStorage() {
  const storeString = localStorage?.getItem(storageKey)
  const storageObj = JSON.parse(storeString || '{}')

  for (const [key, value] of Object.entries(storageObj)) {
    updateReactiveState(key, value)
  }
}

interface PreferencesResponse {
  preferences: State
}

/**
 * Load cloud storage, update local storage and reactive state
 * Save app cloud keys for updating cloud storage items
 */
async function initializeCloudStorage() {
  // check for valid user session
  const isLoggedIn = await hasValidUserSession()
  let preferences = {}

  if (!isLoggedIn) {
    return
  }

  try {
    const data = await httpGetJson<PreferencesResponse>(preferencesUrl)
    preferences = {
      ...data.preferences,
      ...savedItems,
    }
    for (const [key, value] of Object.entries(preferences)) {
      // ignore unregistered keys
      if (!persistentKeys.includes(key)) {
        continue
      }

      updateReactiveState(key, value)
    }

    preferencesResolver(true)
    updateLocalStorage()
  } catch (e) {
    preferencesResolver(false)
  }
}

/**
 * Verify the key is registered in allow list
 * @param key key to verify
 */
export function isAllowed(key: string) {
  return Object.hasOwn(observableState, key)
}

/**
 * Verify the key is for persistent storage
 * @param key key to verify
 * @returns - boolean
 */
function isPersistent(key: string) {
  return persistentKeys.includes(key)
}

/**
 * Migrate old local storage to reactive state and
 * store to new storage object
 */
function migrateOldLocal() {
  // Check if localStorage is available
  if (!localStorage) {
    return
  }

  // Check if has been migrated
  const isMigrated = !!localStorage.getItem(storageKey)

  if (isMigrated) {
    return
  }

  // Check if has old local storage
  const oldLocalStorage = localStorage.getItem(oldStorageKey)

  if (!oldLocalStorage) {
    return
  }

  // Migrate old storage to reactive state
  const storageObj = JSON.parse(oldLocalStorage)

  for (const key in storageObj) {
    // ignore unregistered keys
    if (!isAllowed(key)) {
      continue
    }

    updateReactiveState(key, storageObj[key])
  }

  // Do not remove legacy local storage until fully transitioned
  // localStorage.removeItem(oldStorageKey)
}

/**
 * Add a new item to reactive state if it does not already exist
 *
 * @param key Key for reactive state
 * @param value Value for reactive state
 */
export function initializeItem(key: string, value: unknown) {
  if (!isAllowed(key)) {
    updateReactiveState(key, value)
    saveItem(key, value)
  }
}
